// src/ProductsPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { X, Loader, Package, CheckCircle } from 'lucide-react';
import { API } from './utils/database-frontend.js';

const ProductsPage = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const productsRef = useRef(null);
  
  const isProductsInView = useInView(productsRef, { once: true, threshold: 0.1 });



  // Fetch products from database
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const fetchedProducts = await API.products.getAll();
        console.log('Fetched products from API:', fetchedProducts);
        console.log('First product image_url:', fetchedProducts[0]?.image_url);
        console.log('First product imageUrl:', fetchedProducts[0]?.imageUrl);
        setProducts(fetchedProducts);
        setError(null);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Ürünler yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const openModal = (product) => {
    setSelectedProduct(product);
  };

  const closeModal = () => {
    setSelectedProduct(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-brand-navy via-brand-gray to-brand-navy text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-40"></div>
        
        {/* Animated Background Shapes */}
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-20 right-20 w-40 h-40 bg-brand-orange opacity-10 rounded-full"
        />
        <motion.div
          animate={{ 
            rotate: -360,
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-20 left-20 w-32 h-32 bg-white opacity-5 rounded-full"
        />

        <div className="relative z-10 text-center px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8"
          >
            <div className="inline-flex items-center justify-center w-24 h-24 bg-brand-orange rounded-full mb-6">
              <Package className="w-12 h-12 text-white" />
            </div>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
          >
            Ürünlerimiz
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed text-white"
          >
            Yüksek kaliteli hazır beton ürünleri ile projelerinize değer katıyoruz.
          </motion.p>
        </div>
      </section>



      {/* Products Section */}
      <section ref={productsRef} className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={isProductsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-brand-navy mb-4">Beton Ürün Çeşitlerimiz</h2>
            <p className="text-xl text-brand-gray max-w-2xl mx-auto">
              İhtiyacınıza özel, farklı dayanım sınıflarında hazır beton çözümleri
            </p>
          </motion.div>
        
          {/* Loading State */}
          {loading && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="relative">
                <Loader className="w-12 h-12 text-brand-orange animate-spin" />
                <div className="absolute inset-0 w-12 h-12 border-2 border-brand-orange border-t-transparent rounded-full animate-ping"></div>
              </div>
              <span className="mt-4 text-brand-gray text-lg font-medium">Ürünler yükleniyor...</span>
              <p className="text-brand-gray text-sm mt-2">En güncel ürün bilgilerimizi getiriyoruz</p>
            </motion.div>
          )}

          {/* Error State */}
          {error && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20"
            >
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-8 max-w-md mx-auto">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-red-800 mb-2">Bir Hata Oluştu</h3>
                <p className="text-red-600">{error}</p>
              </div>
            </motion.div>
          )}

          {/* Products Grid */}
          {!loading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.length === 0 ? (
                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="col-span-full text-center py-20"
                >
                  <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl p-12 max-w-md mx-auto">
                    <Package className="w-16 h-16 text-brand-gray mx-auto mb-4 opacity-50" />
                    <h3 className="text-xl font-semibold text-brand-gray mb-2">Henüz Ürün Yok</h3>
                  </div>
                </motion.div>
              ) : (
                products.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                    animate={isProductsInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    whileHover={{ y: -8, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer group border border-gray-100"
                    onClick={() => openModal(product)}
                  >
                    <div className="relative overflow-hidden">
                      <img 
                        src={product.imageUrl || product.image_url || 'https://placehold.co/600x400/f4712f/ffffff?text=Ürün+Görseli'} 
                        alt={product.title}
                        className="w-full h-72 object-contain bg-gradient-to-br from-gray-50 to-gray-100 group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = 'https://placehold.co/600x400/f4712f/ffffff?text=Görsel+Bulunamadı';
                        }}
                      />
                      <div className="absolute top-4 right-4 bg-brand-orange text-white px-3 py-1 rounded-full text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Detay Gör
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-brand-navy mb-3 group-hover:text-brand-orange transition-colors duration-300">
                        {product.title}
                      </h3>
                      <p className="text-brand-gray mb-4 line-clamp-3 leading-relaxed">
                        {product.description.length > 120 
                          ? product.description.substring(0, 120) + '...' 
                          : product.description
                        }
                      </p>

                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header with Image */}
              <div className="relative">
                <div className="bg-gradient-to-br from-brand-navy to-brand-gray p-6 rounded-t-3xl">
                  <img 
                    src={selectedProduct.imageUrl || selectedProduct.image_url || 'https://placehold.co/600x400/f4712f/ffffff?text=Ürün+Görseli'} 
                    alt={selectedProduct.title}
                    className="w-full h-96 object-contain bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl"
                    onError={(e) => {
                      e.target.src = 'https://placehold.co/600x400/f4712f/ffffff?text=Görsel+Bulunamadı';
                    }}
                  />
                </div>
                <button
                  onClick={closeModal}
                  className="absolute top-8 right-8 bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-3 hover:bg-opacity-30 transition duration-300 text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-8">

                
                <h2 className="text-4xl font-bold text-brand-navy mb-6 break-words">
                  {selectedProduct.title}
                </h2>
                
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 mb-6">
                  <h3 className="text-xl font-semibold text-brand-navy mb-4">Ürün Açıklaması</h3>
                  <p className="text-brand-gray text-lg leading-relaxed break-words whitespace-pre-line">
                    {selectedProduct.description}
                  </p>
                </div>


                
                {/* Close Button */}
                <div className="flex justify-center">
                  <button 
                    onClick={closeModal}
                    className="bg-gradient-to-r from-brand-orange to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 px-12 rounded-2xl transition duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductsPage;